import { Card, ConversationRow, SectionLabel } from '@wundo/ui';
import {
  formatMonthAndYear,
  formatRelativeDay,
  meetingTitle,
} from '@/components/agenda/meetingFormat.ts';
import { MeetingKindPill } from '@/components/agenda/MeetingKindPill.tsx';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import type { ConversationMonth } from './groupByMonth.ts';

export interface ConversationListProps {
  months: ConversationMonth[];
  onSelect: (conversation: CalendarMeetingItem) => void;
}

/**
 * The conversation archive: a card of rows per month, newest first.
 *
 * Every row opens the conversation's own page, the way a card on the agenda
 * does, and shows nothing more than what it is called, what kind it is, and
 * when — the preparation, the verslag and the agreements are on that page, and
 * a second copy here would be one more thing to keep true.
 *
 * No status column. Whether a verslag has been written or sent is not something
 * this list can answer honestly, and a guessed status is worse than none.
 */
export function ConversationList({ months, onSelect }: ConversationListProps) {
  return (
    <>
      {months.map(({ key, month, conversations }) => (
        <section key={key} className='mb-6.5'>
          <SectionLabel as='h2' className='mb-2.5 pl-0.5'>
            {formatMonthAndYear(month)}
          </SectionLabel>
          <Card padding='none' className='overflow-hidden'>
            <ul>
              {conversations.map((conversation) => (
                <li
                  key={conversation.id}
                  className='border-t border-fill first:border-t-0'
                >
                  <ConversationRow
                    name={conversationName(conversation)}
                    when={formatRelativeDay(conversation.starts_at)}
                    badge={<MeetingKindPill kind={conversation.kind} />}
                    onClick={() => onSelect(conversation)}
                  />
                </li>
              ))}
            </ul>
          </Card>
        </section>
      ))}
    </>
  );
}

/*
 * What the conversation is called: the name the user gave the thread, falling
 * back to the calendar's title for the row. The thread is the better name — it
 * is the same across every edition of a recurring conversation, where the
 * title changes with whatever the organiser last typed.
 */
function conversationName(conversation: CalendarMeetingItem): string {
  return conversation.thread_name?.trim() || meetingTitle(conversation);
}
